import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import OriginDestinationFilter from './OriginDestinationFilter';

const InlineFilters = React.forwardRef((props, givenRef) => {
  const {
    clearButtonDisplay,
    showLabels,
    locations,
    onClear,
    availableFilters,
    containerSettings,
    clearAllSettings: { showIcon, icon: clearAllIcon, ...clearAllSettings },
    clearAllColumnSettings,
    originDestinationSettings
  } = props;
  const { translate } = React.useContext(Context);
  const ownRef = useRef();
  const ref = givenRef || ownRef;
  const originRef = useRef();
  const [wasClear, setWasClear] = useState(false);
  const locationComponents = {
    ...originDestinationSettings.components,
    origin: {
      // inputFocusReferences: originRef,
      ...originDestinationSettings.components?.origin
    }
  };

  const isDirty = !!(locations.origin.value || locations.destination.value);

  const elementsWrappers = {
    OriginDestination: (e) => {
      return e;
    }
  };

  const elements = {
    OriginDestination: (
      <OriginDestinationFilter
        key="OriginDestination"
        showLabels={showLabels}
        {...originDestinationSettings}
        components={locationComponents}
        {...locations}
        isDirty={wasClear}
        setWasClear={setWasClear}
      />
    )
  };
  const filtersToDisplay = availableFilters.filter((e) => {
    let iterator = e;
    if (typeof iterator === 'object') {
      iterator = e.type;
    }
    return elements[iterator];
  });

  const originFocus = () => {
    originRef.current?.focus();
  };

  React.useImperativeHandle(ref, () => ({
    originFocus
  }));

  const handleClear = () => {
    setWasClear(true);
    setTimeout(() => firstElementRef.current?.focus(), 100);
    onClear();
  };

  const ClearButton = () => (
    <Col sm="12" textAlign="right" {...clearAllColumnSettings}>
      <Button
        paddingWidth="0"
        textTransform="uppercase"
        fontSize="3"
        onClick={handleClear}
        disabled={!isDirty}
        cursor={isDirty ? 'pointer' : 'default'}
        dataTestId="clearAllBtn"
        display="inline-flex"
        alignItems="center"
        {...clearAllSettings}
      >
        {translate('cmp-filter-clear', 'Clear all')}
        {showIcon && <Icon icon="close" paddingLeft="1" size={18} {...clearAllIcon} />}
      </Button>
    </Col>
  );

  const firstElementRef = useRef(null);
  return (
    <>
      <Row gutters marginBottom="2" {...containerSettings} focusReferences={ref}>
        {clearButtonDisplay === 'start' && <ClearButton />}
        {filtersToDisplay.map((ele, index) => {
          let e = ele;
          if (typeof e === 'object') {
            e = e.type;
          }
          const wrapper = elementsWrappers[e];
          const element = wrapper(elements[e]);
          if (index === 0) {
            let temporalElement;
            if (e === 'OriginDestination') {
              if (props.originDestinationSettings.showOrigin) {
                temporalElement = React.cloneElement(elements[e], {
                  components: {
                    ...locationComponents,
                    origin: {
                      ...locationComponents.origin,
                      inputFocusReferences: firstElementRef
                    }
                  }
                });
              } else if (props.originDestinationSettings.showDestination) {
                temporalElement = React.cloneElement(elements[e], {
                  components: {
                    ...locationComponents,
                    destination: {
                      ...locationComponents.destination,
                      inputFocusReferences: firstElementRef
                    }
                  }
                });
              } else {
                temporalElement = React.cloneElement(elements[e], {
                  components: {
                    ...locationComponents,
                    origin: {
                      ...locationComponents.origin,
                      inputFocusReferences: firstElementRef
                    }
                  }
                });
              }
              return wrapper(temporalElement);
            } else {
              return element;
            }
          } else {
            return element;
          }
        })}
        {clearButtonDisplay === 'end' && <ClearButton />}
      </Row>
    </>
  );
});

InlineFilters.displayName = 'InlineFilters';

InlineFilters.propTypes = {
  clearButtonDisplay: PropTypes.oneOf(['start', 'end', 'hidden']),
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(PropTypes.oneOf(['OriginDestination'])),
  containerSettings: PropTypes.objectOf(PropTypes.any),
  clearAllSettings: PropTypes.objectOf(PropTypes.any),
  clearAllColumnSettings: PropTypes.objectOf(PropTypes.any),
  originDestinationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters state and actions
   */
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    destination: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    swapLocations: PropTypes.func
  }),
  onClear: PropTypes.func
};

InlineFilters.defaultProps = {
  clearButtonDisplay: 'hidden',
  availableFilters: ['OriginDestination'],
  showLabels: false,
  containerSettings: {
    padding: '1'
  },
  clearAllSettings: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    showIcon: true,
    borderNone: true,
    icon: {
      iconColor: 'light-18'
    }
  },
  clearAllColumnSettings: {},
  originDestinationSettings: {},
  locations: { origin: {}, destination: {} },
  onClear: null
};

export default InlineFilters;
